import React, { useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useAllInvoiceTemplatesForEntities } from "@api/invoiceTemplate.api";
import { useEntities } from "@hooks/useEntities";
import { BaseFilterProps } from "../../lib";
import { Button } from "@design-system/Button/Button";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";
import errorStyles from "./InvoiceSourceTemplateFilter.module.scss";
import {
    buildInvoiceSourceTemplateOptions,
    resolveTemplateFilterEntityIds,
} from "./invoiceSourceTemplateFilter.lib";

export const InvoiceSourceTemplateFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const businessEntities = useEntities({ onlyBusiness: true });
    const entityIds = useMemo(
        () =>
            resolveTemplateFilterEntityIds({
                selectedEntityIds: filters.entityIds,
                businessEntities,
            }),
        [filters.entityIds, businessEntities],
    );
    const entitiesById = useMemo(
        () => new Map(businessEntities.map((entity) => [entity.id, entity])),
        [businessEntities],
    );

    const { templates, isError, retry } = useAllInvoiceTemplatesForEntities({
        entityIds,
    });

    const selectedIds = filters.sourceTemplateIds;
    const value = selectedIds ?? [];

    const options = useMemo(
        (): FilterMultiselectOption[] =>
            buildInvoiceSourceTemplateOptions({
                templates,
                selectedIds: selectedIds ?? [],
                entitiesById,
            }),
        [templates, selectedIds, entitiesById],
    );

    return (
        <div className={styles.filter}>
            {isError && (
                <div className={errorStyles.error} role="alert">
                    Some templates could not be loaded.
                    <Button variant="link" size="sm" onClick={retry}>
                        Retry
                    </Button>
                </div>
            )}
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={(val) => {
                    const ids = val.map(String);
                    onChange({
                        sourceTemplateIds: ids.length > 0 ? ids : undefined,
                    });
                }}
                name="sourceTemplateIds"
                onBlur={noop}
                searchable
            />
        </div>
    );
};
